import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AdminHero, Segmented, Avatar } from '@/components/admin/ui';

interface Appeal {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  statusAtFiling: 'SUSPENDED' | 'BLOCKED';
  message: string;
  state: 'PENDING' | 'APPROVED' | 'REJECTED';
  decisionNote?: string | null;
  decidedAt?: string | null;
  createdAt: string;
}

export default function Appeals() {
  const qc = useQueryClient();
  const [view, setView] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const { data: rows = [] } = useQuery<Appeal[]>({
    queryKey: ['admin', 'appeals', view],
    queryFn: async () => (await api.get('/admin/appeals', { params: { state: view } })).data,
  });
  const decide = useMutation({
    mutationFn: async ({ id, decision, note }: { id: string; decision: 'APPROVE' | 'REJECT'; note?: string }) =>
      (await api.post(`/admin/appeals/${id}/decide`, { decision, note })).data,
    onSuccess: () => {
      toast.success('Appeal updated.');
      qc.invalidateQueries({ queryKey: ['admin', 'appeals'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update appeal.'),
  });

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Reinstatement"
        title="Appeals"
        subtitle="Suspended or blocked users asking to be reinstated. Approving a block appeal starts the 7-day restore wait automatically."
        right={(
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: 'PENDING', label: 'Pending' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
        )}
      />

      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="card p-8 text-center text-mute text-sm">
            <div className="text-2xl mb-1 opacity-60">📭</div>No {view.toLowerCase()} appeals.
          </div>
        )}
        {rows.map((a) => (
          <AppealCard key={a.id} a={a} busy={decide.isPending} onDecide={(decision, note) => decide.mutate({ id: a.id, decision, note })} />
        ))}
      </div>
    </div>
  );
}

function AppealCard({ a, onDecide, busy }: { a: Appeal; onDecide: (d: 'APPROVE' | 'REJECT', note?: string) => void; busy: boolean }) {
  const [note, setNote] = useState('');
  return (
    <div className="card p-5 border-l-2 border-l-bad/50">
      <div className="flex items-start gap-3.5">
        <Avatar name={a.userName} tone="plum" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium flex flex-wrap items-center gap-x-1.5">
            <span className="pill pill-bad text-[9px]">{a.statusAtFiling}</span>
            {a.userName} <span className="text-mute font-normal">· {a.userEmail}</span>
          </div>
          <p className="text-[13px] text-mute mt-1.5 italic">“{a.message}”</p>
          {a.state !== 'PENDING' && a.decisionNote && (
            <p className="text-[12px] text-mute mt-1.5">Decision note: {a.decisionNote}</p>
          )}
        </div>
        <span className="text-[11px] text-mute shrink-0">{new Date(a.createdAt).toLocaleDateString()}</span>
      </div>
      {a.state === 'PENDING' && (
        <div className="mt-4 flex flex-wrap items-center gap-2 pl-[52px]">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Decision note (optional)" className="input text-[12px] py-1.5 flex-1 min-w-[160px]" />
          <button onClick={() => onDecide('REJECT', note)} disabled={busy} className="btn btn-sm btn-outline">Reject</button>
          <button onClick={() => onDecide('APPROVE', note)} disabled={busy} className="btn btn-sm btn-gold !text-ink">Approve</button>
        </div>
      )}
    </div>
  );
}
